import { useState, useEffect, useRef } from "react";
import { fmt$ } from "./reportUtils";
import Logo from "@/components/Logo";
import { PLANS, FOUNDING_CODE } from "@/config/pricing";

/* ── Shared Plan Cards (DFY section + BottomCTA both use this) ── */
export function DFYPlanCards() {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 14 }}>
      {PLANS.map((plan, i) => (
        <div key={plan.id} style={{ background: "rgba(255,255,255,.05)", border: plan.popular ? "2px solid #10B981" : "1px solid #334155", borderRadius: 10, padding: "22px 20px", position: "relative" }}
          data-testid={`dfy-plan-card-${i}`}>
          {plan.popular && <div style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", background: "#10B981", color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 12px", borderRadius: 4, letterSpacing: ".04em" }}>MOST POPULAR</div>}
          <p style={{ fontSize: 13, fontWeight: 700, color: plan.color, marginBottom: 4 }}>{plan.name}</p>
          {/* Strikethrough regular → founding price */}
          <p style={{ fontSize: 13, color: "#64748B", textDecoration: "line-through", margin: "0 0 2px" }}>
            ${plan.regularPrice}<span style={{ fontSize: 12 }}>{plan.period}</span>
          </p>
          <p style={{ fontSize: 28, fontWeight: 900, color: "#fff", margin: "0 0 2px" }}>
            ${plan.foundingPrice}<span style={{ fontSize: 13, color: "#94A3B8", fontWeight: 500 }}>{plan.period}</span>
          </p>
          <p style={{ fontSize: 10, fontWeight: 700, color: "#FBBF24", letterSpacing: ".04em", marginBottom: 6 }}>
            code {FOUNDING_CODE} — 35% off, locked forever (limited spots)
          </p>
          <p style={{ fontSize: 12, color: "#94A3B8", lineHeight: 1.5, marginBottom: 10 }}>{plan.tagline}</p>
          {plan.trialNote && <p style={{ fontSize: 11, fontWeight: 700, color: "#3ECF8E", marginBottom: 8 }}>{plan.trialNote}</p>}
          <div style={{ height: 1, background: "#334155", margin: "8px 0 10px" }} />
          {plan.features.map((f, j) => (
            <div key={j} style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 5 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={plan.color} strokeWidth="3" style={{ flexShrink: 0, marginTop: 2 }}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
              <span style={{ fontSize: 12, color: "#D1D5DB", lineHeight: 1.4 }}>{f}</span>
            </div>
          ))}
          {/* FOUNDING35 promo code is appended to all Stripe links — coupon must exist in Stripe Dashboard */}
          <button
            onClick={() => window.open(plan.stripeLink, "_blank")}
            data-testid={`dfy-plan-btn-${i}`}
            style={{ width: "100%", marginTop: 14, background: plan.popular ? plan.color : "transparent", border: plan.popular ? "none" : `1px solid ${plan.color}`, color: plan.popular ? "#fff" : plan.color, fontWeight: 700, fontSize: 13, padding: "10px", borderRadius: 8, cursor: "pointer" }}>
            Get Started
          </button>
        </div>
      ))}
    </div>
  );
}

/* ── Countdown Timer Hook ── */
function useCountdown() {
  const [remaining, setRemaining] = useState(() => {
    const stored = sessionStorage.getItem("rankify-countdown-start");
    if (stored) {
      const elapsed = Math.floor((Date.now() - parseInt(stored)) / 1000);
      return Math.max(0, 47 * 3600 + 59 * 60 - elapsed);
    }
    sessionStorage.setItem("rankify-countdown-start", String(Date.now()));
    return 47 * 3600 + 59 * 60; // 47h 59m
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;
  const isUrgent = remaining < 3600; // under 1 hour
  const formatted = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return { formatted, isUrgent, remaining };
}

/* ── Social Proof Hook ── */
function useSocialProof() {
  const [lastUnlock, setLastUnlock] = useState("4 minutes ago");
  const intervalRef = useRef(null);

  useEffect(() => {
    const times = ["2 minutes ago", "4 minutes ago", "7 minutes ago", "3 minutes ago", "5 minutes ago", "1 minute ago", "6 minutes ago"];
    const update = () => setLastUnlock(times[Math.floor(Math.random() * times.length)]);
    // Random interval between 3-8 minutes (180000ms - 480000ms)
    const schedule = () => {
      const delay = 180000 + Math.floor(Math.random() * 300000);
      intervalRef.current = setTimeout(() => { update(); schedule(); }, delay);
    };
    schedule();
    return () => clearTimeout(intervalRef.current);
  }, []);

  return lastUnlock;
}

/* ══════════════════════════════════════════════
   Transition CTA — the main conversion gate
   ══════════════════════════════════════════════ */
export function TransitionCTA({ handleUnlock, checkoutLoading, loss, competitors, businessName, businessScore }) {
  const { formatted, isUrgent } = useCountdown();
  const lastUnlock = useSocialProof();

  // Build competitor leaderboard from real data
  const rivals = (competitors || [])
    .filter(c => !c.is_you)
    .sort((a, b) => (b.total || 0) - (a.total || 0))
    .slice(0, 3);
  const beatingYou = rivals.filter(c => (c.total || 0) > (businessScore || 0)).length;

  return (
    <div data-testid="transition-cta" style={{ background: "linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #111827 100%)", borderRadius: 16, marginBottom: 16, overflow: "hidden", border: "1px solid #334155" }}>

      {/* Countdown Timer Bar */}
      <div data-testid="countdown-timer" style={{ background: isUrgent ? "#7F1D1D" : "#1E293B", borderBottom: "1px solid #334155", padding: "12px 32px", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
        <svg width="16" height="16" fill="none" stroke={isUrgent ? "#FCA5A5" : "#FBBF24"} strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
        <span style={{ fontSize: 13, fontWeight: 700, color: isUrgent ? "#FCA5A5" : "#FDE68A", letterSpacing: ".02em" }}>
          Your audit results are reserved for{" "}
          <span style={{ fontFamily: "monospace", fontSize: 15, fontWeight: 900, color: isUrgent ? "#EF4444" : "#FBBF24", letterSpacing: ".06em" }}>{formatted}</span>
          {" "}— after this your report resets
        </span>
      </div>

      {/* Main CTA Content */}
      <div style={{ padding: "36px 40px", textAlign: "center" }}>
        <div style={{ marginBottom: 16 }}><Logo size="small" theme="dark" /></div>
        <p style={{ fontSize: 14, color: "#94A3B8", marginBottom: 6 }}>You've seen the problems. Now get the fix.</p>
        <h3 style={{ fontSize: 24, fontWeight: 900, color: "#fff", margin: "0 0 8px" }}>Your Step-by-Step Master Plan is Ready</h3>
        <p style={{ fontSize: 15, color: "#CBD5E1", margin: "0 0 28px", lineHeight: 1.7, maxWidth: 500, marginLeft: "auto", marginRight: "auto" }}>
          Unlock the exact blueprint: prioritized fixes, copy-paste templates, and your 90-day roadmap for <strong style={{ color: "#FBBF24" }}>a one-time $27</strong>.
        </p>

        {/* What You'll Unlock Grid */}
        <div data-testid="unlock-benefits" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, marginBottom: 28, textAlign: "left", maxWidth: 640, marginLeft: "auto", marginRight: "auto" }}>
          {[
            ["Full Geo-Grid (all 9 location points)", "#10B981"],
            ["Complete Citation Fix List", "#10B981"],
            ["AI Visibility Score", "#10B981"],
            [loss > 0 ? `${fmt$(loss)} Revenue Recovery Plan` : "Revenue Recovery Plan", "#10B981"],
            ["90-Day Action Roadmap", "#10B981"],
            ["Competitor Gap Analysis", "#10B981"],
          ].map(([text, color], i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "rgba(255,255,255,.04)", borderRadius: 8, border: "1px solid rgba(255,255,255,.06)" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#E2E8F0" }}>{text}</span>
            </div>
          ))}
        </div>

        {/* Competitor Scores Leaderboard */}
        {rivals.length > 0 && (
          <div data-testid="competitor-teaser" style={{ maxWidth: 520, margin: "0 auto 28px", textAlign: "left" }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: "#94A3B8", textAlign: "center", marginBottom: 14, letterSpacing: ".03em" }}>See how you compare to nearby competitors:</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {rivals.map((c, i) => {
                const score = c.total || 0;
                const barColor = score >= 70 ? "#10B981" : score >= 50 ? "#F59E0B" : "#EF4444";
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", background: "rgba(255,255,255,.04)", borderRadius: 8, border: "1px solid rgba(255,255,255,.06)" }}>
                    <span style={{ fontSize: 16, width: 22, textAlign: "center", flexShrink: 0 }}>{i === 0 ? "\uD83C\uDFC6" : "\uD83D\uDCCD"}</span>
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: "#E2E8F0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                    <span style={{ fontSize: 14, fontWeight: 800, color: barColor, minWidth: 52, textAlign: "right" }}>{score}/100</span>
                    <div style={{ width: 80, height: 8, background: "#1E293B", borderRadius: 4, overflow: "hidden", flexShrink: 0 }}>
                      <div style={{ height: "100%", width: `${score}%`, background: barColor, borderRadius: 4 }} />
                    </div>
                  </div>
                );
              })}
              {/* Your business row */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", background: "rgba(239,68,68,.08)", borderRadius: 8, border: "1px solid rgba(239,68,68,.2)" }}>
                <span style={{ fontSize: 16, width: 22, textAlign: "center", flexShrink: 0 }}>&#10067;</span>
                <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: "#FCA5A5", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{businessName || "Your Business"}</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: "#EF4444", minWidth: 52, textAlign: "right" }}>{businessScore || 0}/100</span>
                <div style={{ width: 80, height: 8, background: "#1E293B", borderRadius: 4, overflow: "hidden", flexShrink: 0 }}>
                  <div style={{ height: "100%", width: `${businessScore || 0}%`, background: "#EF4444", borderRadius: 4 }} />
                </div>
              </div>
            </div>
            {beatingYou > 0 && (
              <p style={{ textAlign: "center", fontSize: 13, fontWeight: 700, color: "#FCA5A5", marginTop: 14, lineHeight: 1.6 }}>
                {beatingYou} of your competitors will be found before you.<br />
                <span style={{ color: "#94A3B8", fontWeight: 500 }}>Unlock your full report to see exactly why — $27</span>
              </p>
            )}
          </div>
        )}

        {/* CTA Button */}
        <button onClick={handleUnlock} disabled={checkoutLoading} data-testid="mid-cta-btn"
          style={{ background: "#fff", color: "#111827", fontWeight: 900, fontSize: 17, padding: "16px 48px", borderRadius: 12, border: "none", cursor: checkoutLoading ? "wait" : "pointer", boxShadow: "0 6px 24px rgba(0,0,0,.25)", transition: "transform .15s, box-shadow .15s" }}
          onMouseOver={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 10px 32px rgba(0,0,0,.3)"; }}
          onMouseOut={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,0,0,.25)"; }}>
          {checkoutLoading ? "Processing..." : "Unlock Full Report — $27"}
        </button>
        <p style={{ fontSize: 12, color: "#64748B", marginTop: 12 }}>One-time payment &middot; Instant access &middot; No subscription</p>

        {/* Social Proof Bar */}
        <div data-testid="social-proof" style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 20, marginTop: 24, padding: "16px 0", borderTop: "1px solid #1E293B" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <svg width="14" height="14" fill="none" stroke="#94A3B8" strokeWidth="2" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#94A3B8" }}>2,847 businesses audited this month</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: "#FBBF24", fontSize: 14 }}>&#9733;</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#94A3B8" }}>Last unlock: {lastUnlock}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   DFY (Done-For-You) Services section
   ══════════════════════════════════════════════ */
export function DFYSection() {
  return (
    <div data-testid="dfy-section" style={{ background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)", borderRadius: 12, padding: "36px 32px", marginBottom: 16, border: "1px solid #334155" }}>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: "#94A3B8", letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 8 }}>DONE-FOR-YOU SERVICES</p>
        <h2 style={{ fontSize: 22, fontWeight: 900, color: "#fff", margin: "0 0 8px" }}>Too Busy to Fix This Yourself?</h2>
        <p style={{ fontSize: 14, color: "#94A3B8", lineHeight: 1.7, maxWidth: 500, margin: "0 auto" }}>Our team implements everything in your fix plan — citations, content, technical SEO — so you can focus on running your business.</p>
      </div>
      <DFYPlanCards />
    </div>
  );
}

/* ══════════════════════════════════════════════
   Final CTA footer
   ══════════════════════════════════════════════ */
export function FinalCTA({ hasAccess, overall, proj90, annLoss, missedPct, handleUnlock, checkoutLoading }) {
  const { formatted, isUrgent } = useCountdown();

  return (
    <>
      <div style={{ background: "#111827", borderRadius: 16, padding: "48px 36px", textAlign: "center", marginTop: 8 }}>
        <h2 style={{ fontSize: 26, fontWeight: 900, color: "#fff", margin: "0 0 8px" }}>
          {hasAccess ? "Your Full Report Is Unlocked" : <>Stop Losing <span style={{ color: "#EF4444" }}>{fmt$(annLoss)}</span> Per Year</>}
        </h2>
        <p style={{ fontSize: 15, color: "#9CA3AF", margin: "0 auto 24px", maxWidth: 480, lineHeight: 1.7 }}>
          {hasAccess ? `Follow your 90-day plan step by step. Your score projection: ${overall} → ${proj90}.`
            : `Your competitors take ${missedPct}% of local searches. Your complete fix plan is ready.`}
        </p>
        {!hasAccess && (
          <>
            {/* Countdown in footer CTA */}
            <div style={{ marginBottom: 18 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: isUrgent ? "#EF4444" : "#FDE68A", fontFamily: "monospace", letterSpacing: ".04em" }}>
                {formatted} remaining
              </span>
            </div>
            <button onClick={handleUnlock} disabled={checkoutLoading} data-testid="final-cta-btn"
              style={{ background: "#fff", color: "#111827", fontWeight: 800, fontSize: 16, padding: "16px 44px", borderRadius: 10, border: "none", cursor: "pointer", boxShadow: "0 4px 20px rgba(0,0,0,.2)" }}>
              {checkoutLoading ? "Processing..." : "Unlock Full Report — $27"}
            </button>
            <p style={{ fontSize: 13, color: "#9CA3AF", marginTop: 14 }}>One-time payment &middot; Instant access &middot; No subscription</p>
          </>
        )}
      </div>
      <div style={{ textAlign: "center", padding: "24px 0", fontSize: 12, color: "#4B5563" }}>
        &copy; {new Date().getFullYear()} RankifyLocal. All rights reserved.
      </div>
    </>
  );
}
